import { useTranslation } from "react-i18next";

import type {
  SortOption,
  SortOrder,
} from "../../../ClientPage/Common/controls/MobileSortBottomSheet";

import { ExportCSVButton } from "../../../Button/ExportCSVButton";
import { MobileSortBottomSheet } from "../../../ClientPage/Common/controls/MobileSortBottomSheet";
import { DownloadIcon } from "../../../Icons";

export type { SortOrder };

type MobileSortRowProps = {
  sortBy: string;
  sortOrder: SortOrder;
  onSortChange: (sortBy: string, sortOrder: SortOrder) => void;
  onExport: () => void;
  isExporting: boolean;
};

/**
 * Sort-trigger + Export CSV row. Reuses the existing generic
 * `MobileSortBottomSheet` (field radio group + Highest/Lowest — or
 * Newest/Oldest for the date field — segmented order + Apply) rather than
 * building a new sort sheet, per PART-4052-analyse.md. Export uses the
 * presentational `ExportCSVButton` wired to the context's server-side
 * `exportCsv()` — not `Table/ExportCSV`, which adds client-side CSV generation
 * this tab doesn't need (export happens server-side, not from client-held rows).
 */
export const MobileSortRow = ({
  sortBy,
  sortOrder,
  onSortChange,
  onExport,
  isExporting,
}: MobileSortRowProps) => {
  const { t } = useTranslation();

  const sortOptions: SortOption[] = [
    {
      id: "lastPayment",
      label: t("subIBReport_colLastPayment", "Last payment"),
    },
    { id: "split", label: t("subIBReport_colSplit", "Rebate split") },
    { id: "clients", label: t("subIBReport_colClients", "Clients") },
    {
      id: "totalRebate",
      label: t("subIBReport_colTotalEarning", "Total earnings"),
    },
    { id: "yourPayout", label: t("subIBReport_colYourPayout", "Your payout") },
  ];

  return (
    <div className="flex items-center justify-between gap-4">
      {/* MobileSortBottomSheet's trigger button is `w-full` (it's meant to
          fill its own row in most callers) — contained here in a shrinkable
          flex-1 wrapper so it can't claim the whole row and squeeze Export
          CSV's text onto two lines. */}
      <div className="min-w-0 flex-1">
        <MobileSortBottomSheet
          sortOptions={sortOptions}
          selectedSortBy={sortBy}
          selectedSortOrder={sortOrder}
          onApply={onSortChange}
          title={t("subIBReport_sortBy", "Sort by")}
          isCompact
          getOrderLabels={(id) =>
            id === "lastPayment"
              ? {
                  descLabel: t("subIBReport_sortNewestFirst", "Newest first"),
                  ascLabel: t("subIBReport_sortOldestFirst", "Oldest first"),
                }
              : {
                  descLabel: t("subIBReport_sortHighestFirst", "Highest first"),
                  ascLabel: t("subIBReport_sortLowestFirst", "Lowest first"),
                }
          }
        />
      </div>
      <ExportCSVButton
        size="sm"
        onClick={onExport}
        isLoading={isExporting}
        icon={
          <DownloadIcon
            className="h-4 w-4 [&_path]:stroke-text-primary"
            aria-hidden="true"
          />
        }
        label={t("subIBReport_exportCsv", "Export CSV")}
      />
    </div>
  );
};
